import { get } from '@/services/http';
import {
  mapSuggestionItem,
} from '@/models/inventory_transfer_suggestion';
import type {
  InventoryTransferSuggestionItemAPIInterface,
  InventoryTransferSuggestionItemInterface,
} from '@/models/inventory_transfer_suggestion';

// One FilterColumn condition serialized for the product_filters query param.
// Codes must match the transfer OLAP cube's attribute codes (e.g. vendor is
// 'vendor' here, not the buying list's 'Vendor Name').
export interface ProductFilterCondition {
  name: string;
  code: string;
  value: string[];
  operator: string;
  dataType: string;
}

export interface GetSuggestionItemsParams {
  fromLocationId: number;
  toLocationId: number;
  page?: number;
  perPage?: number;
  search?: string;
  sortBy?: string;
  sortDesc?: boolean;
  priorityLevels?: string[]; // CSV server-side: Critical,High,Normal,Low
  vendor?: string;
  minPrice?: number | null;
  maxPrice?: number | null;
  // Applied server-side inside the OLAP query — unlike the JS-side filters
  // above, these also narrow routeSummary (need_count) and select-all totals.
  productFilters?: ProductFilterCondition[];
  // When true the response includes routeSummary, tallied over the FULL
  // route (product-filtered, but ignoring the JS-side filters above) in the
  // same classification pass — used on each route's first fetch instead of a
  // separate summary endpoint call.
  needCount?: boolean;
}

export interface RouteSummaryInterface {
  countVariants: number;
  totalSuggestedQuantity: number;
  totalPendingQuantity: number;
  noSuggestionSummary: {
    totalExcluded: number;
    reasons: Record<string, number>;
  };
  vendors: string[];
}

export interface SuggestionItemsResponse {
  items: InventoryTransferSuggestionItemInterface[];
  total: number;
  page: number;
  perPage: number;
  routeSummary?: RouteSummaryInterface;
}

export interface SuggestionSelectionResponse {
  variantIds: number[];
  items: InventoryTransferSuggestionItemInterface[];
  total: number;
}

interface RawRouteSummary {
  count_variants: number;
  total_suggested_quantity: number;
  total_pending_quantity: number;
  no_suggestion_summary: {
    total_excluded: number;
    reasons: Record<string, number>;
  };
  vendors: string[];
}

interface RawItemsResponse {
  data: {
    items: InventoryTransferSuggestionItemAPIInterface[];
    total: number;
    page: number;
    per_page: number;
    route_summary?: RawRouteSummary;
  };
}

interface RawSelectionResponse {
  data: {
    variant_ids: Array<number | string>;
    items: InventoryTransferSuggestionItemAPIInterface[];
    total: number;
  };
}

const buildQuery = (params: GetSuggestionItemsParams): Record<string, any> => {
  const query: Record<string, any> = {
    from_location_id: params.fromLocationId,
    to_location_id: params.toLocationId,
  };
  if (params.search && params.search.trim()) {
    query.search = params.search.trim();
  }
  if (params.sortBy) {
    query.sort_by = params.sortBy;
  }
  if (params.sortDesc !== undefined) {
    query.sort_desc = String(params.sortDesc);
  }
  if (params.priorityLevels && params.priorityLevels.length > 0) {
    query.priority_levels = params.priorityLevels.join(',');
  }
  if (params.vendor) {
    query.vendor = params.vendor;
  }
  if (params.minPrice != null && Number.isFinite(params.minPrice)) {
    query.min_price = params.minPrice;
  }
  if (params.maxPrice != null && Number.isFinite(params.maxPrice)) {
    query.max_price = params.maxPrice;
  }
  if (params.productFilters && params.productFilters.length > 0) {
    query.product_filters = JSON.stringify(params.productFilters);
  }
  return query;
};

// Server-side paginated suggestion items for one transfer route (Story 15).
// Stateless fetcher — consumers hold their own per-route paginated state,
// matching the InventoryTransferExcludedItems pattern.
export class InventoryTransferSuggestionItems {
  public static async fetch(
    params: GetSuggestionItemsParams,
  ): Promise<SuggestionItemsResponse> {
    const query = buildQuery(params);
    query.page = params.page ?? 1;
    query.per_page = params.perPage ?? 50;
    if (params.needCount) {
      query.need_count = 'true';
    }

    const response: RawItemsResponse = await get('/inventory-transfers/suggestion-items', query);
    const raw = response.data.route_summary;
    return {
      items: response.data.items.map(mapSuggestionItem),
      total: response.data.total,
      page: response.data.page,
      perPage: response.data.per_page,
      routeSummary: raw ? {
        countVariants: raw.count_variants ?? 0,
        totalSuggestedQuantity: raw.total_suggested_quantity ?? 0,
        totalPendingQuantity: raw.total_pending_quantity ?? 0,
        noSuggestionSummary: {
          totalExcluded: raw.no_suggestion_summary?.total_excluded ?? 0,
          reasons: raw.no_suggestion_summary?.reasons ?? {},
        },
        vendors: raw.vendors ?? [],
      } : undefined,
    };
  }

  // Select-all support: returns ALL items matching the current filters as slim
  // records (enough to register selections and build the transfer payload),
  // without pagination.
  public static async fetchSelection(
    params: GetSuggestionItemsParams,
  ): Promise<SuggestionSelectionResponse> {
    const query = buildQuery(params);
    query.ids_only = 'true';

    const response: RawSelectionResponse = await get('/inventory-transfers/suggestion-items', query);
    return {
      variantIds: (response.data.variant_ids || []).map((id) => Number(id) || 0),
      items: (response.data.items || []).map(mapSuggestionItem),
      total: response.data.total,
    };
  }
}
